import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FiCheck, FiX, FiLoader, FiMail, FiBriefcase, FiAlertCircle } from 'react-icons/fi';
import invitationService from '../../services/invitationService';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton';
import { toast } from 'sonner';

const AcceptInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { currentUser, login, refreshUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [userExists, setUserExists] = useState(false);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);
  
  // Login form state (if user needs to log in)
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  useEffect(() => {
    if (token) {
      validateInvitation();
    }
  }, [token]);

  const validateInvitation = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await invitationService.validateOrganizationInvitation(token);
      
      if (response.success) {
        setInvitation(response.data.invitation);
        setUserExists(response.data.userExists);
        setLoginData(prev => ({ ...prev, email: response.data.invitation.email }));
        
        // If user is already logged in with the correct email, auto-accept
        if (currentUser && currentUser.email === response.data.invitation.email) {
          handleAccept();
        }
      }
    } catch (err) {
      console.error('Invitation validation error:', err);
      setError(err.message || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    try {
      setAccepting(true);
      setError(null);
      
      const response = await invitationService.acceptOrganizationInvitation(token);
      
      if (response.success) {
        setAccepted(true);
        toast.success(response.message || 'Successfully joined organization!');
        
        // Refresh user data to get updated organizationId, accountType, and orgRole
        // This is critical for mode switching to work properly
        try {
          await refreshUser();
          console.log('User data refreshed after accepting invitation');
        } catch (refreshError) {
          console.error('Failed to refresh user data:', refreshError);
          // Continue anyway - user can manually refresh
        }
        
        // Redirect to organization dashboard after 2 seconds
        setTimeout(() => {
          navigate('/org/dashboard');
        }, 2000);
      }
    } catch (err) {
      console.error('Accept invitation error:', err);
      setError(err.message || 'Failed to accept invitation');
      toast.error(err.message || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    try {
      setError(null);
      await login(loginData.email, loginData.password);
      
      // After successful login, accept invitation
      handleAccept();
    } catch (err) {
      setError(err.message || 'Login failed');
      toast.error(err.message || 'Login failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <FiLoader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiAlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Invalid Invitation</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <Link
              to="/auth/login"
              className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCheck className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Welcome to {invitation?.organization?.name}!</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You've successfully joined the organization. Redirecting to dashboard...
            </p>
            <div className="animate-pulse">
              <FiLoader className="w-6 h-6 text-blue-600 animate-spin mx-auto" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show login form if user exists but not logged in
  if (userExists && !currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiBriefcase className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Organization Invitation</h2>
              <p className="text-gray-600 dark:text-gray-400">
                You've been invited to join <strong>{invitation?.organization?.name}</strong>
              </p>
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
                <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={loginData.email}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter your password"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <button
                type="submit"
                disabled={accepting}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {accepting ? 'Processing...' : 'Login & Accept Invitation'}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">or</span>
              </div>
            </div>

            {/* Google Sign In */}
            <GoogleSignInButton 
              redirectAfterAuth={`/invite/${token}`}
              label="Sign in with Google"
            />

            <p className="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">
              Registered with Google? Use the button above to sign in.
            </p>

            <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
              <Link to="/auth/forgot-password" className="text-blue-600 hover:text-blue-700">
                Forgot password?
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show registration prompt if user doesn't exist
  if (!userExists) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiMail className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">You're Invited!</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                <strong>{invitation?.invitedBy?.name || 'Someone'}</strong> has invited you to join{' '}
                <strong>{invitation?.organization?.name}</strong>
              </p>
              
              {invitation?.message && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4 text-left">
                  <p className="text-sm text-blue-800 dark:text-blue-200">{invitation.message}</p>
                </div>
              )}

              <div className="text-left space-y-2 mb-6">
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <FiMail className="w-4 h-4 mr-2" />
                  <span>{invitation?.email}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <FiBriefcase className="w-4 h-4 mr-2" />
                  <span>Role: {invitation?.role || 'Member'}</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Link
                to={`/auth/register?email=${encodeURIComponent(invitation?.email || '')}&token=${token}`}
                className="block w-full px-4 py-2 bg-blue-600 text-white text-center rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Account & Accept
              </Link>

              {/* Divider */}
              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">or</span>
                </div>
              </div>

              <GoogleSignInButton 
                redirectAfterAuth={`/invite/${token}`}
                label="Continue with Google"
              />
              
              <Link
                to="/auth/login"
                className="block w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-center rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                I Already Have an Account
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Current user is logged in with correct email - show accept button
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiBriefcase className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Join {invitation?.organization?.name}</h2>
            <p className="text-gray-600 dark:text-gray-400">
              <strong>{invitation?.invitedBy?.name}</strong> has invited you to join their organization
            </p>
          </div>

          {invitation?.message && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800 dark:text-blue-200">{invitation.message}</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
              <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleAccept}
              disabled={accepting}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {accepting ? (
                <>
                  <FiLoader className="w-4 h-4 mr-2 animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  <FiCheck className="w-4 h-4 mr-2" />
                  Accept Invitation
                </>
              )}
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;
